import { storage } from '@/data/firebase/config';
import { BannerAttachment } from '@/domain/types/collections';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import React, { useState } from 'react';
import { FiLink, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import { MdInsertDriveFile } from 'react-icons/md';
import { toast } from 'react-toastify';

interface BannerAttachmentsProps {
  attachments: BannerAttachment[];
  onChange: (attachments: BannerAttachment[]) => void;
}

const BannerAttachments: React.FC<BannerAttachmentsProps> = ({
  attachments,
  onChange,
}) => {
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleAddLink = () => {
    if (attachments.length > 0) {
      toast.error('Solo puedes agregar un enlace o un archivo', {
        theme: 'colored',
      });
      return;
    }

    if (!linkUrl.trim()) {
      toast.error('La URL es requerida', { theme: 'colored' });
      return;
    }

    if (!linkName.trim()) {
      toast.error('El nombre del enlace es requerido', { theme: 'colored' });
      return;
    }

    // Validar URL
    try {
      new URL(linkUrl);
    } catch {
      toast.error('URL inválida', { theme: 'colored' });
      return;
    }

    const newAttachment: BannerAttachment = {
      type: 'link',
      url: linkUrl,
      name: linkName,
    };

    onChange([...attachments, newAttachment]);
    setLinkUrl('');
    setLinkName('');
    setIsAddingLink(false);
    toast.success('Enlace agregado', { theme: 'colored' });
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar formato (PDF o imagen)
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PDF o imágenes (JPG, PNG, WebP)', {
        theme: 'colored',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `banners/attachments/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error('Error al subir el archivo', { theme: 'colored' });
          setIsUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const newAttachment: BannerAttachment = {
            type: 'file',
            url: downloadURL,
            name: file.name,
            size: file.size,
          };

          onChange([...attachments, newAttachment]);
          toast.success('Archivo subido correctamente', { theme: 'colored' });
          setIsUploading(false);
          setUploadProgress(0);
        },
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error al subir el archivo', { theme: 'colored' });
      setIsUploading(false);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onChange(newAttachments);
    toast.success('Adjunto eliminado', { theme: 'colored' });
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Lista de adjuntos */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center gap-3 flex-1">
                {attachment.type === 'file' ? (
                  <MdInsertDriveFile className="text-primary text-2xl" />
                ) : (
                  <FiLink className="text-gray-600 text-2xl" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">
                    {attachment.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {attachment.type === 'file'
                      ? formatFileSize(attachment.size)
                      : attachment.url}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRemoveAttachment(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Eliminar adjunto"
              >
                <FiTrash2 />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-2">
        <label
          className={`flex-1 ${attachments.length > 0 || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isUploading || attachments.length > 0}
          />
          <div className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
            <FiUpload />
            {isUploading ? `Subiendo... ${uploadProgress}%` : 'Subir archivo'}
          </div>
        </label>

        <button
          onClick={() => setIsAddingLink(!isAddingLink)}
          disabled={attachments.length > 0 && !isAddingLink}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAddingLink ? <FiTrash2 /> : <FiLink />}
          {isAddingLink ? 'Cancelar' : 'Agregar enlace'}
        </button>
      </div>

      {/* Formulario para agregar enlace */}
      {isAddingLink && (
        <div className="border border-gray-300 rounded-lg p-4 space-y-3 bg-gray-50">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del enlace
            </label>
            <input
              type="text"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              placeholder="Ej: Documentación completa"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL
            </label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://ejemplo.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <button
            onClick={handleAddLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <FiPlus />
            Agregar enlace
          </button>
        </div>
      )}

      {/* Info */}
      {attachments.length === 0 && (
        <div className="bg-red-50 border border-primary/20 rounded-lg p-3">
          <p className="text-sm text-primary">
            <strong>Nota:</strong> Puedes agregar <strong>un enlace</strong> o{' '}
            <strong>un archivo</strong> (PDF o imagen JPG/PNG/WebP).
          </p>
        </div>
      )}
    </div>
  );
};

export default BannerAttachments;
